import React, { useState, useEffect, useCallback, useMemo } from "react";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";
import { data } from "./utils/data";

const CityPricing = () => {
  const admin = JSON.parse(localStorage.getItem("admin"));
  const [services, setServices] = useState([]);
  const [subServices, setSubServices] = useState([]);
  const [selectedSubService, setSelectedSubService] = useState(null);
  const [cityPricings, setCityPricings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("name_asc");

  // Modal states
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [showPincodeModal, setShowPincodeModal] = useState(false);
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [pricingToDelete, setPricingToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Form state
  const [pricingForm, setPricingForm] = useState({
    pincode: "",
    city: "",
    state: "",
    zone: "",
    price2hr: "",
    price4hr: "",
    price6hr: "",
    price8hr: "",
  });

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    window.location.href = "/admin/login";
  };

  // Find all matching pincodes from local dataset
  const findAllPincodeMatches = (pincode) => {
    const cleanPin = (pincode || "").trim();
    return data.filter((item) => String(item.Pincode).trim() === cleanPin);
  };

  // Fill city, state, and zone from pincode
  const handleFillDetails = () => {
    if (!pricingForm.pincode.trim()) {
      setError("Please enter a valid 6-digit pincode");
      return;
    }

    const locationMatches = findAllPincodeMatches(pricingForm.pincode);

    if (locationMatches.length === 0) {
      setError(`No location found in database for pincode ${pricingForm.pincode}`);
    } else if (locationMatches.length === 1) {
      // Single match, auto-fill
      const locationData = locationMatches[0];
      setPricingForm((prev) => ({
        ...prev,
        city: locationData.City,
        state: locationData.State,
        zone: locationData.District,
      }));
      setSuccess(`Auto-filled: ${locationData.City}, ${locationData.State}`);
    } else {
      // Multiple matches, show selection modal
      setPincodeOptions(locationMatches);
      setShowPincodeModal(true);
    }
  };

  // Handle pincode selection from modal
  const handlePincodeSelect = (selectedLocation) => {
    setPricingForm((prev) => ({
      ...prev,
      city: selectedLocation.City,
      state: selectedLocation.State,
      zone: selectedLocation.District,
    }));
    setShowPincodeModal(false);
    setPincodeOptions([]);
    setSuccess(`Auto-filled: ${selectedLocation.City}, ${selectedLocation.State}`);
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch all parent services and their sub-services
  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${BASE_URL}admin/services`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const resData = await response.json();
      if (resData.status === "success") {
        setServices(resData.data.services || []);
        // Flatten sub-services with parent info
        const allSubServices = [];
        (resData.data.services || []).forEach((service) => {
          service.subServices?.forEach((subService) => {
            allSubServices.push({
              ...subService,
              serviceName: service.name,
              serviceId: service.id,
            });
          });
        });
        setSubServices(allSubServices);
      } else {
        setError("Failed to fetch services");
      }
    } catch (err) {
      setError("Error fetching services");
      console.error("Services fetch error:", err);
    }
    setLoading(false);
  }, []);

  // Fetch city pricing for the selected sub-service
  const fetchCityPricing = async (subServiceId) => {
    setPricingLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${BASE_URL}admin/sub-services/${subServiceId}/city-pricing`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      const resData = await response.json();
      if (resData.status === "success") {
        setCityPricings(resData.data.cityPricing || []);
        setSelectedSubService(resData.data.subService);
      } else {
        setError("Failed to fetch city pricing");
        setCityPricings([]);
      }
    } catch (err) {
      setError("Error fetching city pricing");
      console.error("City pricing fetch error:", err);
      setCityPricings([]);
    }
    setPricingLoading(false);
    setDropdownLoading(false);
  };

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleSubServiceSelect = (subServiceId) => {
    if (subServiceId) {
      setDropdownLoading(true);
      fetchCityPricing(subServiceId);
    } else {
      setCityPricings([]);
      setSelectedSubService(null);
      setDropdownLoading(false);
    }
  };

  const handlePricingSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSubService) {
      setError("Please select a sub-service first");
      return;
    }

    setLoading(true);

    try {
      let url, method;

      if (editingPricing) {
        url = `${BASE_URL}admin/city-pricing/${editingPricing.id}`;
        method = "PUT";
      } else {
        url = `${BASE_URL}admin/sub-services/${selectedSubService.id}/city-pricing`;
        method = "POST";
      }

      const requestBody = editingPricing
        ? {
            price2hr: parseFloat(pricingForm.price2hr),
            price4hr: parseFloat(pricingForm.price4hr),
            price6hr: parseFloat(pricingForm.price6hr),
            price8hr: parseFloat(pricingForm.price8hr),
          }
        : {
            type: "city",
            city: pricingForm.city.trim(),
            state: pricingForm.state.trim(),
            zone: (pricingForm.zone || "").trim(),
            price2hr: parseFloat(pricingForm.price2hr),
            price4hr: parseFloat(pricingForm.price4hr),
            price6hr: parseFloat(pricingForm.price6hr),
            price8hr: parseFloat(pricingForm.price8hr),
          };

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(requestBody),
      });

      const resData = await response.json();
      if (resData.status === "success") {
        setSuccess(
          `City pricing for ${pricingForm.city || editingPricing.city} ${
            editingPricing ? "updated" : "created"
          } successfully!`,
        );
        setShowPricingModal(false);
        resetPricingForm();
        fetchCityPricing(selectedSubService.id);
      } else {
        setError(
          resData.message ||
            `Failed to ${editingPricing ? "update" : "add"} city pricing`,
        );
      }
    } catch (err) {
      console.error("City pricing submit error:", err);
      setError(`Error ${editingPricing ? "updating" : "adding"} city pricing`);
    }
    setLoading(false);
  };

  const resetPricingForm = () => {
    setPricingForm({
      pincode: "",
      city: "",
      state: "",
      zone: "",
      price2hr: "",
      price4hr: "",
      price6hr: "",
      price8hr: "",
    });
    setEditingPricing(null);
  };

  const openPricingModal = (pricing = null) => {
    if (pricing) {
      setEditingPricing(pricing);
      setPricingForm({
        pincode: "",
        city: pricing.city,
        state: pricing.state,
        zone: pricing.zone || "",
        price2hr: pricing.hourlyPricing
          ? pricing.hourlyPricing["2hr"].toString()
          : (pricing.price2hr || "").toString(),
        price4hr: pricing.hourlyPricing
          ? pricing.hourlyPricing["4hr"].toString()
          : (pricing.price4hr || "").toString(),
        price6hr: pricing.hourlyPricing
          ? pricing.hourlyPricing["6hr"].toString()
          : (pricing.price6hr || "").toString(),
        price8hr: pricing.hourlyPricing
          ? pricing.hourlyPricing["8hr"].toString()
          : (pricing.price8hr || "").toString(),
      });
    } else {
      resetPricingForm();
    }
    setShowPricingModal(true);
  };

  const confirmDeletePricing = async () => {
    if (!pricingToDelete) return;
    setDeleteLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}admin/city-pricing/${pricingToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const resData = await response.json();
      if (resData.status === "success") {
        setSuccess(`Pricing for ${pricingToDelete.city} deleted successfully!`);
        setPricingToDelete(null);
        fetchCityPricing(selectedSubService.id);
      } else {
        setError(resData.message || "Failed to delete city pricing");
      }
    } catch (err) {
      setError("Error deleting city pricing");
      console.error("Delete pricing error:", err);
    }
    setDeleteLoading(false);
  };

  // KPIs derived from current cityPricings
  const kpis = useMemo(() => {
    if (!cityPricings || cityPricings.length === 0) {
      return { totalLocations: 0, min2hr: 0, max8hr: 0, avgHourly: 0 };
    }
    const rates2hr = cityPricings
      .map((p) =>
        p.hourlyPricing ? Number(p.hourlyPricing["2hr"]) : Number(p.price2hr || 0),
      )
      .filter((v) => !isNaN(v) && v > 0);
    const rates8hr = cityPricings
      .map((p) =>
        p.hourlyPricing ? Number(p.hourlyPricing["8hr"]) : Number(p.price8hr || 0),
      )
      .filter((v) => !isNaN(v) && v > 0);

    const min2hr = rates2hr.length > 0 ? Math.min(...rates2hr) : 0;
    const max8hr = rates8hr.length > 0 ? Math.max(...rates8hr) : 0;
    const avg2hr =
      rates2hr.length > 0
        ? Math.round(rates2hr.reduce((a, b) => a + b, 0) / rates2hr.length)
        : 0;
    const avgHourly = Math.round(avg2hr / 2);

    return {
      totalLocations: cityPricings.length,
      min2hr,
      max8hr,
      avgHourly,
    };
  }, [cityPricings]);

  // Filtered and sorted pricings
  const filteredPricings = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    let list = cityPricings.filter(
      (pricing) =>
        (pricing.city || "").toLowerCase().includes(term) ||
        (pricing.state || "").toLowerCase().includes(term) ||
        (pricing.zone || "").toLowerCase().includes(term),
    );

    if (sortOption === "name_asc") {
      list = [...list].sort((a, b) => (a.city || "").localeCompare(b.city || ""));
    } else if (sortOption === "name_desc") {
      list = [...list].sort((a, b) => (b.city || "").localeCompare(a.city || ""));
    } else if (sortOption === "price_asc") {
      list = [...list].sort((a, b) => {
        const pA = a.hourlyPricing ? a.hourlyPricing["2hr"] : a.price2hr;
        const pB = b.hourlyPricing ? b.hourlyPricing["2hr"] : b.price2hr;
        return (Number(pA) || 0) - (Number(pB) || 0);
      });
    } else if (sortOption === "price_desc") {
      list = [...list].sort((a, b) => {
        const pA = a.hourlyPricing ? a.hourlyPricing["2hr"] : a.price2hr;
        const pB = b.hourlyPricing ? b.hourlyPricing["2hr"] : b.price2hr;
        return (Number(pB) || 0) - (Number(pA) || 0);
      });
    }

    return list;
  }, [cityPricings, searchTerm, sortOption]);

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-stone-900 flex flex-col font-sans">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Content Area */}
      <main className="lg:ml-72 min-h-screen flex flex-col flex-1 pb-16">
        {/* Top Header */}
        <header className="bg-white border-b border-stone-200 px-4 sm:px-6 lg:px-8 py-5 sticky top-0 z-30 shadow-xs">
          <div className="pt-16 lg:pt-0 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">
                <span>Admin</span>
                <span>/</span>
                <span>Pricing Management</span>
                <span>/</span>
                <span className="text-stone-900">City & Regional Rates</span>
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold text-stone-900 tracking-tight flex items-center gap-3">
                <span>City & Regional Pricing</span>
                {selectedSubService && (
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-stone-100 text-stone-700 border border-stone-200">
                    {cityPricings.length} {cityPricings.length === 1 ? "Location" : "Locations"}
                  </span>
                )}
              </h1>
              <p className="text-sm text-stone-500 mt-1">
                Configure regional hourly rates (2h, 4h, 6h, 8h) per trade and service across India.
              </p>
            </div>

            {/* Quick Action Button */}
            <div className="flex items-center gap-3">
              {selectedSubService && (
                <button
                  onClick={() => openPricingModal()}
                  disabled={selectedSubService.isUniversal}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-sm ${
                    selectedSubService.isUniversal
                      ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                      : "bg-[#F8D346] text-[#1C1917] hover:bg-[#edd03e] active:scale-[0.98]"
                  }`}
                  title={
                    selectedSubService.isUniversal
                      ? "Universal services do not support regional pricing"
                      : "Add new city pricing tier"
                  }
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  <span>Add City Pricing</span>
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Floating Notification / Alerts */}
        <div className="px-4 sm:px-6 lg:px-8 pt-4">
          {error && (
            <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm font-medium flex items-center justify-between shadow-xs animate-fadeIn">
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-red-600 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </div>
              <button
                onClick={() => setError("")}
                className="text-red-500 hover:text-red-700 text-sm font-semibold"
              >
                ✕
              </button>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium flex items-center justify-between shadow-xs animate-fadeIn">
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-emerald-600 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{success}</span>
              </div>
              <button
                onClick={() => setSuccess("")}
                className="text-emerald-500 hover:text-emerald-700 text-sm font-semibold"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        {/* Page Content Container */}
        <div className="px-4 sm:px-6 lg:px-8 pt-2 space-y-6">
          {/* Sub-Service Selector Card */}
          <div className="bg-white rounded-2xl border border-stone-200 p-5 lg:p-6 shadow-xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <label className="text-sm font-bold text-stone-900 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#F8D346]"></span>
                Select Sub-Service / Trade
              </label>
              {subServices.length > 0 && (
                <span className="text-xs text-stone-500">
                  {subServices.length} sub-services available across {services.length} categories
                </span>
              )}
            </div>

            <div className="relative">
              <select
                value={selectedSubService?.id || ""}
                onChange={(e) => handleSubServiceSelect(e.target.value)}
                disabled={loading || dropdownLoading}
                className="w-full bg-stone-50 border border-stone-300 hover:border-stone-400 text-stone-900 rounded-xl px-4 py-3.5 text-base font-medium focus:outline-none focus:ring-2 focus:ring-[#F8D346] focus:border-[#F8D346] transition-all cursor-pointer"
              >
                <option value="">
                  {loading
                    ? "Loading service catalog..."
                    : "— Select a Sub-Service to view & configure pricing —"}
                </option>
                {services.map((service) => {
                  const items = subServices.filter(
                    (sub) => sub.serviceId === service.id,
                  );
                  if (items.length === 0) return null;
                  return (
                    <optgroup key={service.id} label={service.name}>
                      {items.map((sub) => (
                        <option key={sub.id} value={sub.id}>
                          {service.name} → {sub.name}{" "}
                          {sub.isUniversal ? "(Universal Pricing)" : "(Regional)"}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>

              {dropdownLoading && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 bg-white/90 px-3 py-1 rounded-lg border border-stone-200 text-xs font-semibold text-stone-600">
                  <div className="w-3.5 h-3.5 border-2 border-stone-300 border-t-[#1C1917] rounded-full animate-spin"></div>
                  <span>Loading Pricing...</span>
                </div>
              )}
            </div>
          </div>

          {/* Sub-Service Context Banner */}
          {selectedSubService && (
            <div className="bg-white rounded-2xl border border-stone-200 p-5 lg:p-6 shadow-xs">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-stone-100 border border-stone-200 flex-shrink-0 overflow-hidden flex items-center justify-center">
                    {selectedSubService.imageUrl ? (
                      <img
                        src={selectedSubService.imageUrl}
                        alt={selectedSubService.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center text-stone-400">
                        <svg
                          className="w-7 h-7"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-stone-500">
                        {selectedSubService.service?.name || selectedSubService.serviceName || "Category"}
                      </span>
                      <span className="text-stone-300">•</span>
                      <span
                        className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${
                          selectedSubService.isActive
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {selectedSubService.isActive ? "Active Sub-Service" : "Inactive"}
                      </span>
                    </div>
                    <h2 className="text-xl font-bold text-stone-900 mt-0.5">
                      {selectedSubService.name}
                    </h2>
                    {selectedSubService.description && (
                      <p className="text-xs text-stone-500 mt-0.5 max-w-xl line-clamp-1">
                        {selectedSubService.description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {selectedSubService.isUniversal ? (
                    <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs font-semibold">
                      <svg
                        className="w-4 h-4 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <span>Universal Standard Pricing (Global)</span>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs font-semibold">
                      <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span>Regional Multi-City Pricing Active</span>
                    </div>
                  )}

                  {selectedSubService.defaultPrice > 0 && (
                    <div className="px-3.5 py-2 rounded-xl bg-stone-100 border border-stone-200 text-stone-800 text-xs font-semibold">
                      Base Default: ₹{selectedSubService.defaultPrice}
                    </div>
                  )}
                </div>
              </div>

              {selectedSubService.isUniversal && (
                <div className="mt-4 p-3.5 bg-amber-50/80 rounded-xl border border-amber-200 text-amber-800 text-xs flex items-center gap-2">
                  <svg
                    className="w-4 h-4 flex-shrink-0 text-amber-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>
                    This sub-service is set to <strong>Universal Pricing</strong>. It uses a single global rate across all regions, and custom city-by-city pricing is disabled. To enable city pricing, edit this sub-service under Services.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Real Metrics KPI Overview (if locations exist) */}
          {selectedSubService && cityPricings.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-xs">
                <div className="flex items-center justify-between text-stone-500 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Configured Cities
                  </span>
                  <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center text-stone-700">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-2xl font-bold text-stone-900">
                  {kpis.totalLocations}
                </div>
                <div className="text-xs text-stone-500 mt-1">Active regional zones</div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-xs">
                <div className="flex items-center justify-between text-stone-500 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Min Base (2h)
                  </span>
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-2xl font-bold text-stone-900">
                  ₹{kpis.min2hr}
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  ₹{Math.round(kpis.min2hr / 2)}/hr base rate
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-xs">
                <div className="flex items-center justify-between text-stone-500 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Max Tier (8h)
                  </span>
                  <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-2xl font-bold text-stone-900">
                  ₹{kpis.max8hr}
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  ₹{Math.round(kpis.max8hr / 8)}/hr full shift
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-xs">
                <div className="flex items-center justify-between text-stone-500 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Avg Hourly Rate
                  </span>
                  <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-2xl font-bold text-stone-900">
                  ₹{kpis.avgHourly}
                  <span className="text-xs font-normal text-stone-500">/hr</span>
                </div>
                <div className="text-xs text-stone-500 mt-1">Across all cities</div>
              </div>
            </div>
          )}

          {/* Search, Filter & Controls Bar */}
          {selectedSubService && (
            <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="relative w-full sm:flex-1">
                <svg
                  className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Filter by city name, state, or district/zone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-stone-50 border border-stone-300 hover:border-stone-400 rounded-xl text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346] focus:border-[#F8D346] transition-all"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 text-xs font-bold"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-xs font-semibold text-stone-500 whitespace-nowrap">
                    Sort by:
                  </span>
                  <select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    className="bg-stone-50 border border-stone-300 text-stone-800 text-xs font-medium rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                  >
                    <option value="name_asc">City Name (A → Z)</option>
                    <option value="name_desc">City Name (Z → A)</option>
                    <option value="price_asc">2-Hr Rate (Low → High)</option>
                    <option value="price_desc">2-Hr Rate (High → Low)</option>
                  </select>
                </div>

                <div className="text-xs font-semibold text-stone-500 whitespace-nowrap hidden lg:block">
                  Showing {filteredPricings.length} of {cityPricings.length}
                </div>
              </div>
            </div>
          )}

          {/* Pricing Grid / Empty States */}
          {!selectedSubService ? (
            <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center shadow-xs">
              <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 text-[#1C1917] flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-[#1C1917]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.75}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.75}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-stone-900">
                No Sub-Service Selected
              </h3>
              <p className="text-sm text-stone-500 mt-1 max-w-md mx-auto">
                Please choose a sub-service (trade) from the dropdown above to view, configure, and manage city-specific hourly rates.
              </p>
            </div>
          ) : pricingLoading ? (
            <div className="bg-white rounded-2xl border border-stone-200 p-16 text-center shadow-xs flex flex-col items-center justify-center">
              <div className="w-10 h-10 border-3 border-stone-200 border-t-[#1C1917] rounded-full animate-spin mb-4"></div>
              <p className="text-sm font-semibold text-stone-700">
                Loading regional pricing data...
              </p>
            </div>
          ) : filteredPricings.length === 0 ? (
            <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center shadow-xs">
              <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4 text-stone-400">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-stone-900">
                {searchTerm
                  ? "No matching locations found"
                  : "No City Pricing Configured Yet"}
              </h3>
              <p className="text-sm text-stone-500 mt-1 max-w-md mx-auto">
                {searchTerm
                  ? `No pricing records match '${searchTerm}'. Try searching for another city or state.`
                  : `Currently there are no regional rates configured for ${selectedSubService.name}. Add rates for Jaipur, Delhi, Mumbai, or custom postal zones.`}
              </p>
              {!selectedSubService.isUniversal && (
                <button
                  onClick={() => openPricingModal()}
                  className="mt-5 inline-flex items-center gap-2 bg-[#F8D346] text-[#1C1917] hover:bg-[#edd03e] font-semibold text-sm px-5 py-2.5 rounded-xl shadow-xs transition-all"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  <span>Add First City Pricing</span>
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredPricings.map((pricing) => {
                const p2 = pricing.hourlyPricing
                  ? pricing.hourlyPricing["2hr"]
                  : pricing.price2hr;
                const p4 = pricing.hourlyPricing
                  ? pricing.hourlyPricing["4hr"]
                  : pricing.price4hr;
                const p6 = pricing.hourlyPricing
                  ? pricing.hourlyPricing["6hr"]
                  : pricing.price6hr;
                const p8 = pricing.hourlyPricing
                  ? pricing.hourlyPricing["8hr"]
                  : pricing.price8hr;

                return (
                  <div
                    key={pricing.id}
                    className="bg-white rounded-2xl border border-stone-200 hover:border-stone-300 shadow-xs hover:shadow-md transition-all flex flex-col justify-between overflow-hidden group"
                  >
                    {/* Top Location Bar */}
                    <div className="p-5 border-b border-stone-100 bg-stone-50/50">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-lg font-bold text-stone-900 leading-tight">
                              {pricing.city}
                            </h4>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-stone-200/80 text-stone-700">
                              {pricing.state}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-stone-500 mt-1">
                            <svg
                              className="w-3.5 h-3.5 text-stone-400 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                            <span className="font-medium">
                              Zone: {pricing.zone || "Universal / All Districts"}
                            </span>
                          </div>
                        </div>

                        {/* Card Actions */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => openPricingModal(pricing)}
                            className="p-2 rounded-lg bg-white border border-stone-200 text-stone-600 hover:bg-[#F8D346] hover:text-[#1C1917] hover:border-[#F8D346] transition-colors"
                            title="Edit Rates"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => setPricingToDelete(pricing)}
                            className="p-2 rounded-lg bg-white border border-stone-200 text-stone-400 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-colors"
                            title="Delete Rates"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Hourly Pricing 4-Tier Matrix */}
                    <div className="p-5 space-y-4">
                      <div className="grid grid-cols-2 gap-2.5">
                        <div className="bg-stone-50 border border-stone-200/80 rounded-xl p-3 text-center">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500 block">
                            2 Hours
                          </span>
                          <span className="text-base font-extrabold text-stone-900 block mt-0.5">
                            ₹{p2}
                          </span>
                          <span className="text-[10px] text-stone-400 block">
                            ₹{Math.round(Number(p2) / 2)}/hr
                          </span>
                        </div>

                        <div className="bg-stone-50 border border-stone-200/80 rounded-xl p-3 text-center">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500 block">
                            4 Hours
                          </span>
                          <span className="text-base font-extrabold text-stone-900 block mt-0.5">
                            ₹{p4}
                          </span>
                          <span className="text-[10px] text-stone-400 block">
                            ₹{Math.round(Number(p4) / 4)}/hr
                          </span>
                        </div>

                        <div className="bg-stone-50 border border-stone-200/80 rounded-xl p-3 text-center">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500 block">
                            6 Hours
                          </span>
                          <span className="text-base font-extrabold text-stone-900 block mt-0.5">
                            ₹{p6}
                          </span>
                          <span className="text-[10px] text-stone-400 block">
                            ₹{Math.round(Number(p6) / 6)}/hr
                          </span>
                        </div>

                        <div className="bg-stone-50 border border-stone-200/80 rounded-xl p-3 text-center">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500 block">
                            8 Hours (Full)
                          </span>
                          <span className="text-base font-extrabold text-stone-900 block mt-0.5">
                            ₹{p8}
                          </span>
                          <span className="text-[10px] text-stone-400 block">
                            ₹{Math.round(Number(p8) / 8)}/hr
                          </span>
                        </div>
                      </div>

                      {/* Pricing Analysis Pill if available */}
                      {pricing.pricingAnalysis && (
                        <div className="bg-stone-100/70 rounded-xl p-2.5 flex items-center justify-between text-xs">
                          <span className="text-stone-500 font-medium">Rate Spread:</span>
                          <div className="flex items-center gap-3 font-semibold text-stone-700">
                            <span>Min: ₹{pricing.pricingAnalysis.minPrice}</span>
                            <span>•</span>
                            <span>
                              Avg: ₹{Math.round(pricing.pricingAnalysis.averagePrice)}
                            </span>
                            <span>•</span>
                            <span>Max: ₹{pricing.pricingAnalysis.maxPrice}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Card Footer */}
                    <div className="px-5 py-3 bg-stone-50/40 border-t border-stone-100 flex items-center justify-between text-[11px] text-stone-400">
                      <span>ID: {pricing.id?.slice(-8) || "N/A"}</span>
                      <span>
                        Updated:{" "}
                        {pricing.updatedAt
                          ? new Date(pricing.updatedAt).toLocaleDateString()
                          : "Active"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Add / Edit Pricing Modal */}
      {showPricingModal && (
        <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-stone-200">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-stone-200 flex items-center justify-between bg-stone-50/50">
              <div>
                <h3 className="text-lg font-bold text-stone-900">
                  {editingPricing ? "Edit City Pricing" : "Add New City Pricing"}
                </h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  For Sub-Service: <span className="font-semibold text-stone-800">{selectedSubService?.name}</span>
                </p>
              </div>
              <button
                onClick={() => setShowPricingModal(false)}
                className="w-8 h-8 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-500 flex items-center justify-center font-bold text-sm transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handlePricingSubmit} className="p-6 space-y-5">
              {/* Pincode Auto-Fill Section (Only when creating) */}
              {!editingPricing && (
                <div className="bg-stone-50 p-4 rounded-xl border border-stone-200">
                  <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1.5">
                    Fast Auto-Fill by PIN Code
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      maxLength={6}
                      value={pricingForm.pincode}
                      onChange={(e) =>
                        setPricingForm((prev) => ({
                          ...prev,
                          pincode: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                      className="flex-1 px-3.5 py-2.5 bg-white border border-stone-300 rounded-xl text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                      placeholder="e.g. 302021"
                    />
                    <button
                      type="button"
                      onClick={handleFillDetails}
                      className="px-4 py-2.5 bg-stone-900 hover:bg-stone-800 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5"
                    >
                      <svg
                        className="w-3.5 h-3.5 text-[#F8D346]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <span>Lookup Location</span>
                    </button>
                  </div>
                  <p className="text-[11px] text-stone-400 mt-1.5">
                    Searches India post directory for automatic City, State & District fill.
                  </p>
                </div>
              )}

              {/* Location Fields */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">
                      City Name *
                    </label>
                    <input
                      type="text"
                      required
                      disabled={Boolean(editingPricing)}
                      value={pricingForm.city}
                      onChange={(e) =>
                        setPricingForm((prev) => ({ ...prev, city: e.target.value }))
                      }
                      placeholder="e.g. Jaipur"
                      className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#F8D346] ${
                        editingPricing
                          ? "bg-stone-100 border-stone-200 text-stone-500 cursor-not-allowed"
                          : "bg-white border-stone-300 text-stone-900"
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">
                      State *
                    </label>
                    <input
                      type="text"
                      required
                      disabled={Boolean(editingPricing)}
                      value={pricingForm.state}
                      onChange={(e) =>
                        setPricingForm((prev) => ({ ...prev, state: e.target.value }))
                      }
                      placeholder="e.g. Rajasthan"
                      className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#F8D346] ${
                        editingPricing
                          ? "bg-stone-100 border-stone-200 text-stone-500 cursor-not-allowed"
                          : "bg-white border-stone-300 text-stone-900"
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">
                    District / Zone (Optional)
                  </label>
                  <input
                    type="text"
                    disabled={Boolean(editingPricing)}
                    value={pricingForm.zone}
                    onChange={(e) =>
                      setPricingForm((prev) => ({ ...prev, zone: e.target.value }))
                    }
                    placeholder="e.g. Jaipur Nagar Nigam / South Zone"
                    className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#F8D346] ${
                      editingPricing
                        ? "bg-stone-100 border-stone-200 text-stone-500 cursor-not-allowed"
                        : "bg-white border-stone-300 text-stone-900"
                    }`}
                  />
                  {editingPricing && (
                    <p className="text-[11px] text-stone-400 mt-1">
                      Location cannot be modified when editing existing tier.
                    </p>
                  )}
                </div>
              </div>

              {/* 4 Hourly Rates Matrix */}
              <div className="pt-2 border-t border-stone-200">
                <label className="block text-xs font-bold uppercase tracking-wider text-stone-900 mb-3">
                  Hourly Pricing Tiers (Gross INR)
                </label>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1">
                      2 Hours Rate (₹) *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 font-bold text-sm">
                        ₹
                      </span>
                      <input
                        type="number"
                        required
                        min="1"
                        step="1"
                        value={pricingForm.price2hr}
                        onChange={(e) =>
                          setPricingForm((prev) => ({
                            ...prev,
                            price2hr: e.target.value,
                          }))
                        }
                        placeholder="e.g. 500"
                        className="w-full pl-8 pr-3.5 py-2.5 bg-white border border-stone-300 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1">
                      4 Hours Rate (₹) *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 font-bold text-sm">
                        ₹
                      </span>
                      <input
                        type="number"
                        required
                        min="1"
                        step="1"
                        value={pricingForm.price4hr}
                        onChange={(e) =>
                          setPricingForm((prev) => ({
                            ...prev,
                            price4hr: e.target.value,
                          }))
                        }
                        placeholder="e.g. 900"
                        className="w-full pl-8 pr-3.5 py-2.5 bg-white border border-stone-300 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1">
                      6 Hours Rate (₹) *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 font-bold text-sm">
                        ₹
                      </span>
                      <input
                        type="number"
                        required
                        min="1"
                        step="1"
                        value={pricingForm.price6hr}
                        onChange={(e) =>
                          setPricingForm((prev) => ({
                            ...prev,
                            price6hr: e.target.value,
                          }))
                        }
                        placeholder="e.g. 1300"
                        className="w-full pl-8 pr-3.5 py-2.5 bg-white border border-stone-300 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1">
                      8 Hours Rate (₹) *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 font-bold text-sm">
                        ₹
                      </span>
                      <input
                        type="number"
                        required
                        min="1"
                        step="1"
                        value={pricingForm.price8hr}
                        onChange={(e) =>
                          setPricingForm((prev) => ({
                            ...prev,
                            price8hr: e.target.value,
                          }))
                        }
                        placeholder="e.g. 1600"
                        className="w-full pl-8 pr-3.5 py-2.5 bg-white border border-stone-300 rounded-xl text-sm font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                      />
                    </div>
                  </div>
                </div>

                {/* Efficiency calculation preview */}
                {pricingForm.price2hr > 0 && pricingForm.price8hr > 0 && (
                  <div className="mt-3 p-3 bg-stone-50 rounded-xl border border-stone-200 text-xs text-stone-600 flex items-center justify-between">
                    <span>Hourly Rate Efficiency:</span>
                    <span className="font-semibold text-stone-900">
                      ₹{Math.round(pricingForm.price2hr / 2)}/hr (2h base) → ₹
                      {Math.round(pricingForm.price8hr / 8)}/hr (8h shift)
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="pt-3 border-t border-stone-200 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowPricingModal(false)}
                  className="flex-1 py-2.5 px-4 rounded-xl border border-stone-300 hover:bg-stone-100 text-stone-700 text-sm font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-2.5 px-4 rounded-xl bg-[#F8D346] hover:bg-[#edd03e] text-[#1C1917] text-sm font-bold shadow-xs transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading && (
                    <div className="w-4 h-4 border-2 border-[#1C1917] border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {editingPricing ? "Update Pricing" : "Save City Pricing"}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Multiple Pincode Matches Modal */}
      {showPincodeModal && (
        <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-stone-200 overflow-hidden">
            <div className="p-5 border-b border-stone-200 bg-stone-50/50">
              <h3 className="text-base font-bold text-stone-900">
                Multiple Locations Found
              </h3>
              <p className="text-xs text-stone-500 mt-0.5">
                PIN code <span className="font-bold text-stone-800">{pricingForm.pincode}</span> matches {pincodeOptions.length} areas. Please select one:
              </p>
            </div>

            <div className="p-5 max-h-80 overflow-y-auto space-y-2.5">
              {pincodeOptions.map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handlePincodeSelect(opt)}
                  className="w-full text-left p-3.5 rounded-xl border border-stone-200 hover:border-[#F8D346] hover:bg-amber-50/30 transition-all group"
                >
                  <div className="font-bold text-sm text-stone-900 group-hover:text-stone-900">
                    {opt.PostOfficeName}
                  </div>
                  <div className="text-xs text-stone-500 mt-1 flex items-center gap-2">
                    <span>{opt.City}</span>
                    <span>•</span>
                    <span>{opt.District}</span>
                    <span>•</span>
                    <span className="font-medium text-stone-700">{opt.State}</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="p-4 border-t border-stone-200 bg-stone-50/50">
              <button
                type="button"
                onClick={() => {
                  setShowPincodeModal(false);
                  setPincodeOptions([]);
                }}
                className="w-full py-2.5 px-4 bg-white border border-stone-300 hover:bg-stone-100 text-stone-700 rounded-xl text-xs font-semibold transition-colors"
              >
                Cancel Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {pricingToDelete && (
        <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full border border-stone-200 p-6">
            <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </div>

            <h3 className="text-center font-bold text-stone-900 text-lg">
              Delete City Pricing?
            </h3>
            <p className="text-center text-xs text-stone-500 mt-1">
              Are you sure you want to remove regional pricing for{" "}
              <strong className="text-stone-800">
                {pricingToDelete.city}, {pricingToDelete.state}
              </strong>
              {pricingToDelete.zone && ` (${pricingToDelete.zone})`}?
            </p>

            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                disabled={deleteLoading}
                onClick={() => setPricingToDelete(null)}
                className="flex-1 py-2.5 px-4 rounded-xl border border-stone-300 hover:bg-stone-100 text-stone-700 text-xs font-semibold transition-all"
              >
                Keep Pricing
              </button>
              <button
                type="button"
                disabled={deleteLoading}
                onClick={confirmDeletePricing}
                className="flex-1 py-2.5 px-4 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition-all flex items-center justify-center gap-2"
              >
                {deleteLoading && (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>Confirm Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CityPricing;
